import { inject, Injectable } from '@angular/core';
import { cloneDeep } from 'lodash-es';

import { AuxIconName } from '@shared/components/icon/icon';
import { LocalStorageKey } from '@shared/constants/localStorageKey';
import { CMC_EXCLUDED_URLS } from '@shared/constants/routingPath';
import { Flags, LaunchDarklyService } from '@shared/services/launch-darkly.service';
import { MainQuery } from '@shared/store/main/main.query';
import { TrialsQuery } from '@shared/store/trials/trials.query';

import {
  NavigationMenuCost,
  NavigationMenuItem,
} from '../../navigation-menu/constants/navigation-menu.const';
import { SearchResult } from '../search-modal.component';

export interface CategorizedResults {
  actions: SearchAction[];
  pages: SearchResult[];
  recents: SearchResult[];
}

export interface SearchAction {
  action: () => void;
  description?: string;
  icon?: AuxIconName;
  name: string;
}

interface FlatNavigationItem {
  icon?: AuxIconName;
  isLeaf: boolean;
  name: string;
  path?: string;
  url: string;
}

@Injectable({
  providedIn: 'root',
})
export class NavigationSearchService {
  launchDarklyService = inject(LaunchDarklyService);
  mainQuery = inject(MainQuery);
  trialsQuery = inject(TrialsQuery);

  readonly MAX_RECENT_PAGES = 5;
  recentPages: SearchResult[] = [];

  addToRecents(page: SearchResult): void {
    // Remove if already exists
    this.recentPages = this.recentPages.filter((p) => p.url !== page.url);

    // Add to beginning
    this.recentPages.unshift(page);

    // Keep only max items
    if (this.recentPages.length > this.MAX_RECENT_PAGES) {
      this.recentPages = this.recentPages.slice(0, this.MAX_RECENT_PAGES);
    }

    // Save to localStorage
    this.saveRecentPagesToStorage();
  }

  private calculateSearchScore(item: FlatNavigationItem, searchTerms: string[]): number {
    const itemName = item.name.toLowerCase();
    const itemPath = item.path?.toLowerCase() || '';

    let score = 0;

    for (const term of searchTerms) {
      // Exact match in name gets highest score
      if (itemName.includes(term)) {
        if (itemName === term) {
          score += 100; // Exact match
        } else if (itemName.startsWith(term)) {
          score += 50; // Starts with term
        } else {
          score += 20; // Contains term
        }
      }

      // Match in path gets lower score
      if (itemPath.includes(term)) {
        score += 10;
      }

      // Fuzzy matching - check if characters appear in order
      if (this.fuzzyMatch(itemName, term)) {
        score += 5;
      }
    }

    return score;
  }

  constructor() {
    this.loadRecentPagesFromStorage();
  }

  private filterByFlagsAndTrialType(
    config: NavigationMenuItem[],
    flags: Flags,
    isTrialTypeCmc: boolean
  ): NavigationMenuItem[] {
    return config.filter((item) => {
      if (isTrialTypeCmc && this.isCmcExcluded(item.url)) {
        return false;
      }

      // Skip dividers
      if (item.divider) {
        return false;
      }

      const hasRequiredFlags = this.hasRequiredFlags(item, flags);

      if (hasRequiredFlags) {
        if (item.subRoutes?.length) {
          item.subRoutes = this.filterByFlagsAndTrialType(item.subRoutes, flags, isTrialTypeCmc);
        }

        if (item.flyOutMenu?.length) {
          item.flyOutMenu = this.filterByFlagsAndTrialType(item.flyOutMenu, flags, isTrialTypeCmc);
        }

        return true;
      }

      return false;
    });
  }

  private flattenNavigationItems(
    items: NavigationMenuItem[],
    parentPath = ''
  ): FlatNavigationItem[] {
    const result: FlatNavigationItem[] = [];

    for (const item of items) {
      if (item.divider) {
        continue;
      }

      const currentPath = parentPath ? `${parentPath}/${item.name}` : item.name;
      const hasChildren = (item.subRoutes?.length || 0) + (item.flyOutMenu?.length || 0) > 0;
      const isLeaf = !hasChildren;

      // Only add leaf items (no children)
      if (isLeaf) {
        result.push({
          icon: item.svg,
          isLeaf: true,
          name: item.name,
          path: parentPath || undefined,
          url: item.url,
        });
      }

      // Process sub-routes
      if (item.subRoutes?.length) {
        result.push(...this.flattenNavigationItems(item.subRoutes, currentPath));
      }

      // Process flyout menu items
      if (item.flyOutMenu?.length) {
        result.push(...this.flattenNavigationItems(item.flyOutMenu, currentPath));
      }
    }

    return result;
  }

  private fuzzyMatch(text: string, pattern: string): boolean {
    const textChars = text.toLowerCase();
    const patternChars = pattern.toLowerCase();

    let textIndex = 0;
    let patternIndex = 0;

    while (textIndex < textChars.length && patternIndex < patternChars.length) {
      if (textChars[textIndex] === patternChars[patternIndex]) {
        patternIndex++;
      }

      textIndex++;
    }

    return patternIndex === patternChars.length;
  }

  private getActions(): SearchAction[] {
    return [
      {
        action: () => {},
        description: 'Add a new vendor budget to the system',
        icon: 'Plus',
        name: 'Create new Vendor Budget',
      },
      {
        action: () => {},
        description: 'Register a new vendor in the system',
        icon: 'User',
        name: 'Add Vendor',
      },
      {
        action: () => {},
        description: 'Upload files to document library',
        icon: 'Upload',
        name: 'Upload Documents',
      },
      {
        action: () => {},
        description: 'Export data to Excel or CSV',
        icon: 'Download',
        name: 'Export Data',
      },
    ];
  }

  private getFilteredNavigationItems(): NavigationMenuItem[] {
    const flags = this.launchDarklyService.flags$.getValue();
    const isCurrentTrialCMC = this.trialsQuery.isCurrentTrialCMC(
      this.mainQuery.mainState().trialKey
    )();

    return this.filterByFlagsAndTrialType(
      cloneDeep(NavigationMenuCost.ROUTER_CONFIG),
      flags,
      isCurrentTrialCMC
    );
  }

  private hasRequiredFlags(item: NavigationMenuItem, flags: Flags): boolean {
    // If no flags specified, include item
    if (!item.flagKey && !item.flagKeys?.length) {
      return true;
    }

    const flagIsOn = !item.flagKey || Boolean(flags[item.flagKey]);
    const someOfRelatedFlagsIsOn =
      !item.flagKeys?.length || item.flagKeys.some((flagKey) => Boolean(flags[flagKey]));

    return flagIsOn && someOfRelatedFlagsIsOn;
  }

  private isCmcExcluded(url: string): boolean {
    return CMC_EXCLUDED_URLS.includes(url);
  }

  private loadRecentPagesFromStorage(): void {
    try {
      const stored = localStorage.getItem(LocalStorageKey.SEARCH_RECENT_PAGES);

      if (stored) {
        const parsedPages: SearchResult[] = JSON.parse(stored);

        // Validate that these pages still exist in the current navigation
        const validPages = this.validateStoredPages(parsedPages);

        this.recentPages = validPages.slice(0, this.MAX_RECENT_PAGES);
      }
    } catch (error) {
      console.warn('Failed to load recent pages from localStorage:', error);
      this.recentPages = [];
    }
  }

  private saveRecentPagesToStorage(): void {
    try {
      localStorage.setItem(LocalStorageKey.SEARCH_RECENT_PAGES, JSON.stringify(this.recentPages));
    } catch (error) {
      console.warn('Failed to save recent pages to localStorage:', error);
    }
  }

  // Backward compatibility
  search(query: string): SearchResult[] {
    const results = this.searchCategorized(query);

    return [...results.recents, ...results.pages];
  }

  searchCategorized(query: string): CategorizedResults {
    let actions = this.getActions();

    // TODO: Uncomment this when we have actions
    actions = [];

    // Get the filtered navigation items to use as source of truth for what's allowed
    const filteredNavigation = this.getFilteredNavigationItems();
    const flatItems = this.flattenNavigationItems(filteredNavigation);
    const allowedUrls = new Set(flatItems.map((item) => item.url));

    // Filter recents to only include URLs that exist in the current filtered navigation
    const filteredRecents = this.recentPages.filter((recent) => allowedUrls.has(recent.url));

    if (!query || query.trim().length === 0) {
      // When no search query, show all navigation items
      const topPages = flatItems.map(({ isLeaf: _, ...item }) => item);

      return {
        actions,
        pages: topPages,
        recents: filteredRecents,
      };
    }

    const searchTerms = query
      .toLowerCase()
      .split(' ')
      .filter((term) => term.length > 0);

    // Search pages
    const pageResults = flatItems
      .map((item) => {
        const score = this.calculateSearchScore(item, searchTerms);

        return { ...item, score };
      })
      .filter((item) => item.score > 0)
      .sort((a, b) => b.score - a.score)
      .map(({ isLeaf: _, score: __, ...item }) => item);

    // Filter actions by search terms
    const filteredActions = actions.filter((action) =>
      searchTerms.some(
        (term) =>
          action.name.toLowerCase().includes(term) ||
          action.description?.toLowerCase().includes(term)
      )
    );

    // Filter recents by search terms (using the already CMC-filtered recents)
    const searchFilteredRecents = filteredRecents.filter((recent) =>
      searchTerms.some(
        (term) =>
          recent.name.toLowerCase().includes(term) || recent.path?.toLowerCase().includes(term)
      )
    );

    return {
      actions: filteredActions,
      pages: pageResults,
      recents: searchFilteredRecents,
    };
  }

  private validateStoredPages(storedPages: SearchResult[]): SearchResult[] {
    // Get current navigation items to validate against
    const filteredNavigation = this.getFilteredNavigationItems();
    const flatItems = this.flattenNavigationItems(filteredNavigation);

    // Create a set of valid URLs for quick lookup
    const validUrls = new Set(flatItems.map((item) => item.url));

    // Filter stored pages to only include those that still exist in navigation
    return storedPages.filter((page) => validUrls.has(page.url));
  }
}
